if (!customElements.get("product-tab-nav")) {
  const DESKTOP_QUERY = "(min-width: 1068px)";
  const MOBILE_QUERY = "(max-width: 767px)";

  function isMobileProductTabViewport() {
    return window.matchMedia(MOBILE_QUERY).matches;
  }

  function getStickyHeaderOffset() {
    const header = document.querySelector(".header");
    if (!header) return 0;

    const style = getComputedStyle(header);
    if (style.position === "fixed" || style.position === "sticky" || header.classList.contains("is-sticky")) {
      return Math.ceil(header.getBoundingClientRect().height);
    }

    return 0;
  }

  function scrollPageToProductTabSection(nav) {
    const section = nav.closest(".section-product-tab");
    const header = section?.querySelector(".section-header");
    if (!header) return;

    const offset = getStickyHeaderOffset() + 8;
    const headerTop = header.getBoundingClientRect().top;
    const needsScroll = headerTop < offset || headerTop > window.innerHeight * 0.4;

    if (!needsScroll) return;

    const top = Math.max(0, window.scrollY + headerTop - offset);
    window.scrollTo({ top, behavior: "smooth" });
  }

  function scrollActiveTabLinkIntoView(nav, button) {
    const scroller = nav.classList.contains("product-tab__list") ? nav : nav.querySelector(".product-tab__list");
    if (!scroller || !button) return;

    const scrollerRect = scroller.getBoundingClientRect();
    const buttonRect = button.getBoundingClientRect();
    const edgePadding = 8;

    if (buttonRect.left < scrollerRect.left + edgePadding) {
      scroller.scrollTo({
        left: scroller.scrollLeft - (scrollerRect.left + edgePadding - buttonRect.left),
        behavior: "smooth",
      });
      return;
    }

    if (buttonRect.right > scrollerRect.right - edgePadding) {
      scroller.scrollTo({
        left: scroller.scrollLeft + (buttonRect.right - scrollerRect.right + edgePadding),
        behavior: "smooth",
      });
    }
  }

  function scrollTabLinkIntoView(nav, button) {
    if (!button) return;

    scrollActiveTabLinkIntoView(nav, button);

    requestAnimationFrame(() => {
      scrollActiveTabLinkIntoView(nav, button);
    });
  }

  function scrollMobileTabIntoView(nav, button) {
    scrollTabLinkIntoView(nav, button);

    if (!isMobileProductTabViewport()) return;

    requestAnimationFrame(() => {
      scrollPageToProductTabSection(nav);
    });
  }

  function measureProductTabCellHeight(cell) {
    if (!cell) return 0;

    const content =
      cell.querySelector("product-card") ||
      cell.querySelector(".product-card") ||
      cell.firstElementChild;

    if (!content) return Math.ceil(cell.getBoundingClientRect().height);

    const saved = {
      cellMinHeight: cell.style.minHeight,
      cellHeight: cell.style.height,
      contentHeight: content instanceof HTMLElement ? content.style.height : "",
      contentMinHeight: content instanceof HTMLElement ? content.style.minHeight : "",
    };

    cell.style.minHeight = "0";
    cell.style.height = "auto";

    if (content instanceof HTMLElement) {
      content.style.height = "auto";
      content.style.minHeight = "0";
    }

    const height = Math.ceil(content.getBoundingClientRect().height);

    cell.style.minHeight = saved.cellMinHeight;
    cell.style.height = saved.cellHeight;

    if (content instanceof HTMLElement) {
      content.style.height = saved.contentHeight;
      content.style.minHeight = saved.contentMinHeight;
    }

    return height;
  }

  function syncProductTabCarouselHeight(flkty) {
    if (!flkty?.viewport || flkty.cells.length === 0) return;

    requestAnimationFrame(() => {
      let maxHeight = 0;

      flkty.cells.forEach(({ element }) => {
        maxHeight = Math.max(maxHeight, measureProductTabCellHeight(element));
      });

      if (maxHeight > 0) {
        flkty.viewport.style.height = `${maxHeight}px`;
      }
    });
  }

  function bindProductTabCarouselHeightSync(nav, slider, flkty) {
    if (!flkty) return;
    if (nav._productTabHeightSyncFlkty === flkty) return;

    nav._productTabHeightSyncFlkty = flkty;
    nav._syncProductTabCarouselHeight = () => syncProductTabCarouselHeight(flkty);

    const sync = nav._syncProductTabCarouselHeight;
    sync();
    flkty.on("select", sync);
    flkty.on("settle", sync);
    flkty.on("ready", sync);

    slider?.querySelectorAll("img").forEach((img) => {
      if (img.complete) return;
      img.addEventListener("load", sync, { once: true });
    });
  }

  class ProductTabNav extends HTMLElement {    constructor() {
      super();
      this.carousel = null;
    }

    connectedCallback() {
      this.buttons = Array.from(this.querySelectorAll("button"));
      this.links = this.closest(".section-header")?.querySelectorAll(".linked-to-tab") || [];
      this.target = this.dataset.target;

      if (typeof PeekCarousel === "undefined") {
        console.warn("[product-tab] Load peek-carousel.js before product-tab.js");
        return;
      }

      this.carousel = new PeekCarousel.Controller(this, {
        getSlider: () => this.getSlider(),
        gapScope: ".section-product-tab",
        gapVar: "--product-tab-gap",
        gapDefault: 16,
        slideSelector: ".product-tab__cell",
        staticClass: "product-tab__grid--static",
        carouselClass: "product-tab__mode-carousel",
        wrapAround: false,
        adaptiveHeight: false,
        syncHeight: false,
        reuseFlickity: false,
        autoMarkSlides: false,
        removeSlideClassOnStatic: true,
        onBeforeStatic: (slider) => this.setVisibleProducts(slider, this.getActiveHandle()),
        onBeforeCarousel: (slider) => this.setVisibleProducts(slider, this.getActiveHandle()),
        onAfterCarousel: (slider, flkty) => bindProductTabCarouselHeightSync(this, slider, flkty),
      });

      this.buttons.forEach((button, i) => {
        button.addEventListener("click", (event) => {
          event.preventDefault();
          this.activateTab(button, i);
        });
      });

      this.carousel.init();

      const activeButton = this.querySelector(".product-tab__list-link.active");
      if (activeButton) {
        scrollTabLinkIntoView(this, activeButton);
      }

      if (Shopify.designMode) {
        this.addEventListener("shopify:block:select", (event) => {
          const index = this.buttons.indexOf(event.target);
          if (index >= 0) this.buttons[index].click();
        });
      }
    }

    disconnectedCallback() {
      this._productTabHeightSyncFlkty = null;
      this._syncProductTabCarouselHeight = null;
      this.carousel?.destroy();
      this.carousel = null;
    }

    getSlider() {
      return document.getElementById(this.target);
    }

    getActiveHandle() {
      const activeButton = this.querySelector(".product-tab__list-link.active");
      return activeButton?.dataset.collection || this.buttons[0]?.dataset.collection || null;
    }

    isDesktop() {
      return this.carousel?.isDesktop() ?? window.matchMedia(DESKTOP_QUERY).matches;
    }

    setVisibleProducts(slider, handle) {
      if (!slider || !handle) return;

      slider.querySelectorAll(".product-tab__cell").forEach((el) => {
        const show = el.dataset.collection === handle;
        el.classList.toggle("product-tab__visible", show);

        if (this.isDesktop()) {
          el.classList.remove("carousel__slide");
          return;
        }

        el.classList.toggle("carousel__slide", show);
      });
    }

    updateLayout() {
      this.carousel?.updateLayout();
      this._syncProductTabCarouselHeight?.();
    }

    activateTab(button, index) {
      this.buttons.forEach((el) => el.classList.remove("active"));
      button.classList.add("active");

      const handle = button.dataset.collection;
      if (this.links.length && handle) {
        this.links.forEach((el) => el.classList.remove("active"));
        const matchingLink = Array.from(this.links).find((el) => el.dataset.collection === handle);
        matchingLink?.classList.add("active");
      }

      if (button.dataset.collection) {
        this.toggleCollection(button.dataset.collection);
      }

      scrollMobileTabIntoView(this, button);
    }

    toggleCollection(handle) {
      const slider = this.getSlider();
      if (!slider) return;

      if (this.isDesktop()) {
        this.setVisibleProducts(slider, handle);
        return;
      }

      const flkty = this.carousel?.getFlickity();
      if (!flkty) {
        this.carousel?.updateLayout();
        return;
      }

      const hiddenProducts = slider.querySelectorAll(`.product-tab__cell:not([data-collection="${handle}"])`);
      const activeProducts = slider.querySelectorAll(`[data-collection="${handle}"]`);

      hiddenProducts.forEach((el) => {
        el.classList.remove("carousel__slide", "product-tab__visible");
        slider.append(el);
      });

      activeProducts.forEach((el) => {
        el.classList.add("carousel__slide", "product-tab__visible");
      });

      flkty.insert(activeProducts);
      flkty.reloadCells();
      flkty.select(0, 0, 1);
      this.carousel.applySlideWidths(flkty);
      this._syncProductTabCarouselHeight?.();
    }
  }

  customElements.define("product-tab-nav", ProductTabNav);

  if (!window.__productTabResizeBound) {
    window.__productTabResizeBound = true;
    window.addEventListener("resize", () => {
      clearTimeout(window.__productTabResizeTimer);
      window.__productTabResizeTimer = setTimeout(() => {
        document.querySelectorAll(".section-product-tab product-tab-nav").forEach((nav) => {
          nav.updateLayout?.();
        });
      }, 150);
    });
  }
}
